import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { Link, Redirect } from "react-router-dom";

import { uniqBy } from "lodash";

import { useHistory, useLocation } from "react-router";

import useWTMPageStyles from "./useWTMPageStyles";

import useListActiveDestinations from "../queries/useListActiveDestinations";
import useWTMURLs from "../useWTMURLs";
import useFeatureFlags from "../../hooks/useFeatureFlag";

import useDestinationValidity from "../../hooks/useDestinationValidity";
import { SaaSDestination } from "../../destinations/destinations";
import useAllSaasDestinationsByType from "../../hooks/useAllSaasDestinationsByType";
import { allowlistBuiltInProps } from "../../PHIAllowListPage/PHIAllowListEditModal";
import Risk from "../Risk";
import {
  DestinationValidationResult,
  DestinationValidity,
} from "../../destinations/validation";
import {
  LoadedDestinationInfo,
  PropertySensitivityLevels,
} from "../../types/global";
import PropChips, {
  PropChipType,
  sortPropsFn,
} from "./PHIAllowListTab/PropChips";
import PropsFilterSelect, {
  ALL_OPTION_VALUE,
  AllowListProp,
} from "./PHIAllowListTab/PropsFilterSelect";
import hasValueIntersection from "./PHIAllowListTab/hasValueIntersection";
import DestinationLogo from "./DestinationLogo";
import ColumnLabelWithTooltip from "../ColumnLabelWithTooltip";
import renderCSV from "./PHIAllowListTab/renderCSV";
import FormLabelWithTooltip from "./FormLabelWithTooltip";
import { theme } from "../../theme";

function computePropsSet(
  destination: LoadedDestinationInfo,
  props: PropertySensitivityLevels = {},
  type: PropChipType
): Set<AllowListProp> {
  const propNames = Object.keys(props)
    .map((value) => {
      const displayName =
        value in allowlistBuiltInProps
          ? allowlistBuiltInProps[value].beautifulName
          : undefined;

      const label = displayName !== undefined ? displayName : value;

      return {
        destination: destination.name,
        destinationId: destination.id,
        displayName,
        label,
        value,
        type,
      };
    })
    .sort(sortPropsFn);

  return new Set(propNames);
}

function configInvalidText(validity: DestinationValidity): string {
  switch (validity) {
    case DestinationValidity.totallyInvalidClientModeWhenHIPAA:
      return "Client Mode not HIPAA-Compliant";
    case DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA:
      return "Missing HIPAA Allowlist";
    case DestinationValidity.partiallyInvalid:
      return "Partially Invalid Config";
    case DestinationValidity.totallyInvalid:
      return "Invalid Config";
    default:
      return "";
  }
}

const allEventPropsOption: AllowListProp & {
  value: typeof ALL_OPTION_VALUE;
} = {
  destination: "",
  destinationId: "",
  displayName: "",
  label: "All Event Properties",
  value: ALL_OPTION_VALUE,
  type: "event",
};

const allUserPropsOption: AllowListProp & {
  value: typeof ALL_OPTION_VALUE;
} = {
  destination: "",
  destinationId: "",
  displayName: "",
  label: "All User Properties",
  value: ALL_OPTION_VALUE,
  type: "user",
};

const allGroupPropsOption: AllowListProp & {
  value: typeof ALL_OPTION_VALUE;
} = {
  destination: "",
  destinationId: "",
  displayName: "",
  label: "All Group Properties",
  value: ALL_OPTION_VALUE,
  type: "group",
};

export function allowListProps(
  destination: LoadedDestinationInfo,
  props: PropertySensitivityLevels = {},
  type: PropChipType
): AllowListProp[] {
  return Object.keys(props)
    .map((value) => {
      const displayName =
        value in allowlistBuiltInProps
          ? allowlistBuiltInProps[value].beautifulName
          : undefined;

      const label =
        displayName !== undefined ? `${displayName} (${value})` : value;

      return {
        destination: destination.name,
        destinationId: destination.id,
        displayName,
        label,
        value,
        type,
      };
    })
    .sort(sortPropsFn);
}

type PHIAllowListRow = {
  destinationMetadata: SaaSDestination;
  searchKey: string;
  validity: DestinationValidationResult;
  activeDestination: LoadedDestinationInfo;
  eventProps: Set<AllowListProp>;
  groupProps: Set<AllowListProp>;
  userProps: Set<AllowListProp>;
};

export function PHIAllowListTab() {
  const classes = useWTMPageStyles();
  const history = useHistory();
  const location = useLocation();
  const wtmURLs = useWTMURLs();
  const flags = useFeatureFlags();
  const destinationValidity = useDestinationValidity();
  const allDestinationsByType = useAllSaasDestinationsByType();

  const destinationFilter: string | null = useMemo(() => {
    const rawQueryParams = new URLSearchParams(location.search);

    return rawQueryParams.get("destination");
  }, [location.search]);

  const setDestinationFilter = (destination: string | null) => {
    const rawQueryParams = new URLSearchParams(location.search);
    if (destination === null) {
      rawQueryParams.delete("destination");
    } else {
      rawQueryParams.set("destination", destination);
    }

    history.push(location.pathname + "?" + rawQueryParams.toString());
  };

  const [searchString, setSearchString] = useState<string>("");

  const [selectedEventProps, setSelectedEventProps] = useState<
    Set<AllowListProp>
  >(new Set([allEventPropsOption]));

  const [selectedGroupProps, setSelectedGroupProps] = useState<
    Set<AllowListProp>
  >(new Set([allGroupPropsOption]));

  const [selectedUserProps, setSelectedUserProps] = useState<
    Set<AllowListProp>
  >(new Set([allUserPropsOption]));

  const {
    data: activeDestinations,
    isLoading: isLoadingActiveDestinations,
  } = useListActiveDestinations(1000);

  const phiAllowListRows: PHIAllowListRow[] = useMemo(() => {
    if (activeDestinations === undefined || isLoadingActiveDestinations) {
      return [];
    }

    return activeDestinations.destinations
      .filter((activeDest) => activeDest.name in allDestinationsByType)
      .map((activeDestination) => {
        const destinationMetadata = allDestinationsByType[
          activeDestination.name
        ] as SaaSDestination;

        const validity = destinationValidity(
          activeDestination,
          allDestinationsByType[activeDestination.name] as SaaSDestination
        );

        const eventProps = computePropsSet(
          activeDestination,
          activeDestination.event_property_sensitivity_levels,
          "event"
        );
        const groupProps = computePropsSet(
          activeDestination,
          activeDestination.group_property_sensitivity_levels,
          "group"
        );
        const userProps = computePropsSet(
          activeDestination,
          activeDestination.user_property_sensitivity_levels,
          "user"
        );

        const searchKey = `${activeDestination.name} ${[
          ...eventProps,
          ...groupProps,
          ...userProps,
        ]
          .map((p) => p.label)
          .join(" ")}`.toLowerCase();

        return {
          activeDestination,
          destinationMetadata,
          eventProps,
          userProps,
          groupProps,
          searchKey,
          validity,
        };
      });
  }, [
    activeDestinations,
    isLoadingActiveDestinations,
    allDestinationsByType,
    destinationValidity,
  ]);

  const eventPropsFilterOptions = useMemo(() => {
    const allProps = phiAllowListRows.reduce((acc, row) => {
      return [
        ...acc,
        ...allowListProps(
          row.activeDestination,
          row.activeDestination.event_property_sensitivity_levels,
          "event"
        ),
      ];
    }, [] as AllowListProp[]);

    return uniqBy(allProps, (p) => p.value);
  }, [phiAllowListRows]);

  const userPropsFilterOptions = useMemo(() => {
    const allProps = phiAllowListRows.reduce((acc, row) => {
      return [
        ...acc,
        ...allowListProps(
          row.activeDestination,
          row.activeDestination.user_property_sensitivity_levels,
          "user"
        ),
      ];
    }, [] as AllowListProp[]);

    return uniqBy(allProps, (p) => p.value);
  }, [phiAllowListRows]);

  const groupPropsFilterOptions = useMemo(() => {
    const allProps = phiAllowListRows.reduce((acc, row) => {
      return [
        ...acc,
        ...allowListProps(
          row.activeDestination,
          row.activeDestination.group_property_sensitivity_levels,
          "group"
        ),
      ];
    }, [] as AllowListProp[]);

    return uniqBy(allProps, (p) => p.value);
  }, [phiAllowListRows]);

  const hasSelectedProps = useMemo(() => {
    return (
      [
        ...selectedEventProps,
        ...selectedGroupProps,
        ...selectedUserProps,
      ].filter((p) => p.value !== ALL_OPTION_VALUE).length > 0
    );
  }, [selectedEventProps, selectedGroupProps, selectedUserProps]);

  const filteredRows = useMemo(() => {
    return phiAllowListRows.filter((r) => {
      if (
        destinationFilter !== null &&
        r.activeDestination.name !== destinationFilter
      ) {
        return false;
      }

      if (
        searchString !== "" &&
        r.searchKey.includes(searchString.toLowerCase())
      ) {
        return true;
      }

      const matchesEvents =
        (eventPropsFilterOptions.length > 0 &&
          selectedEventProps.has(allEventPropsOption)) ||
        hasValueIntersection(selectedEventProps, r.eventProps);

      const matchesGroups =
        (groupPropsFilterOptions.length > 0 &&
          selectedGroupProps.has(allGroupPropsOption)) ||
        hasValueIntersection(selectedGroupProps, r.groupProps);

      const matchesUsers =
        (userPropsFilterOptions.length > 0 &&
          selectedUserProps.has(allUserPropsOption)) ||
        hasValueIntersection(selectedUserProps, r.userProps);

      return matchesEvents || matchesGroups || matchesUsers;
    });
  }, [
    destinationFilter,
    eventPropsFilterOptions.length,
    groupPropsFilterOptions.length,
    phiAllowListRows,
    searchString,
    selectedEventProps,
    selectedGroupProps,
    selectedUserProps,
    userPropsFilterOptions.length,
  ]);

  const CardTitle = () => {
    const numRowsShown = filteredRows.length;
    const numRowsTotal = phiAllowListRows.length;
    const numRowsText =
      numRowsShown === numRowsTotal
        ? `${numRowsTotal} destinations`
        : `${numRowsShown} of ${numRowsTotal} destinations`;

    return <>PHI Allowlist: {numRowsText}</>;
  };

  const handleClickDownloadCSV = () => {
    const allProps = phiAllowListRows.reduce((acc, row) => {
      return [
        ...acc,
        ...Array.from(row.eventProps),
        ...Array.from(row.userProps),
        ...Array.from(row.groupProps),
      ];
    }, [] as AllowListProp[]);

    const csvRows = renderCSV(allProps);

    /**
     * Credit to the following StackOverflow post for the following code snippet!
     *
     * @see https://stackoverflow.com/questions/14964035/how-to-export-javascript-array-info-to-csv-on-client-side
     */
    const encoded = encodeURI(`data:text/csv;charset=utf-8,${csvRows}`);
    const link = document.createElement("a");
    link.setAttribute("href", encoded);
    link.setAttribute("download", `hipaa-allowlist-${new Date().toJSON()}.csv`);
    link.style.display = "none";
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  if (!flags("hipaa") && !flags("hipaaV2")) {
    return <Redirect to={wtmURLs.trackersOverview} />;
  }

  function handleOnDelete(prop: AllowListProp) {
    switch (prop.type) {
      case "event":
        selectedEventProps.delete(prop);
        setSelectedEventProps(new Set(selectedEventProps));
        break;
      case "group":
        selectedGroupProps.delete(prop);
        setSelectedGroupProps(new Set(selectedGroupProps));
        break;
      case "user":
        selectedUserProps.delete(prop);
        setSelectedUserProps(new Set(selectedUserProps));
        break;
    }
    selectedEventProps.delete(prop);
  }

  return (
    <Card style={{ overflow: "visible" }}>
      <CardHeader
        className={classes.cardHeader}
        title={
          <Grid
            container
            spacing={0}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              <CardTitle />
            </Grid>

            <Grid item>
              <Tooltip title="Export the HIPAA Allowlist as a CSV file" arrow>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleClickDownloadCSV}
                >
                  Export CSV
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        }
      />

      <Divider />

      <CardContent className={classes.cardContent}>
        <nav className={classes.cardNav}>
          <Grid
            container
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item container xs={9} spacing={1}>
              <Grid item xs={3}>
                <PropsFilterSelect
                  label="Event Properties"
                  allOption={allEventPropsOption}
                  props={eventPropsFilterOptions}
                  selectedProps={selectedEventProps}
                  onChange={(selectedProps) => {
                    setSelectedEventProps(selectedProps);
                  }}
                  onClickClearButton={() => setSelectedEventProps(new Set())}
                />
              </Grid>

              <Grid item xs={3}>
                <PropsFilterSelect
                  label="User Properties"
                  allOption={allUserPropsOption}
                  props={userPropsFilterOptions}
                  selectedProps={selectedUserProps}
                  onChange={(selectedProps) => {
                    setSelectedUserProps(selectedProps);
                  }}
                  onClickClearButton={() => setSelectedUserProps(new Set())}
                />
              </Grid>

              <Grid item xs={3}>
                <PropsFilterSelect
                  label="Group Properties"
                  allOption={allGroupPropsOption}
                  props={groupPropsFilterOptions}
                  selectedProps={selectedGroupProps}
                  onChange={(selectedProps) => {
                    setSelectedGroupProps(selectedProps);
                  }}
                  onClickClearButton={() => {
                    setSelectedGroupProps(new Set());
                  }}
                />
              </Grid>

              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <FormLabelWithTooltip
                    label="Select Destination"
                    tooltip="Filter list to only show a specific destination's PHI Allowlist"
                  />

                  <Select
                    multiple={false}
                    value={
                      destinationFilter === null
                        ? "__ALL_DESTINATIONS__"
                        : destinationFilter
                    }
                    variant="outlined"
                    onChange={(e) => {
                      const newDestination = e.target.value as string;
                      if (newDestination === "__ALL_DESTINATIONS__") {
                        setDestinationFilter(null);
                        return;
                      }

                      setDestinationFilter(newDestination);
                    }}
                    style={{
                      padding: theme.spacing(0.1971875),
                    }}
                  >
                    <MenuItem value="__ALL_DESTINATIONS__">
                      All Destinations
                    </MenuItem>
                    {activeDestinations?.destinations.map((dest) => {
                      return (
                        <MenuItem key={dest.id} value={dest.name}>
                          {dest.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid item xs={2}>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant="overline">Search</Typography>
                </FormLabel>
                <TextField
                  fullWidth
                  placeholder="Search..."
                  size="small"
                  type="search"
                  value={searchString}
                  variant="outlined"
                  onChange={(e) => setSearchString(e.target.value)}
                />
              </FormControl>
            </Grid>
            {destinationFilter !== null && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel>
                    <Typography variant="overline">
                      Destination Filter
                    </Typography>
                  </FormLabel>

                  <div>
                    <Chip
                      label={destinationFilter}
                      size="small"
                      onDelete={() => setDestinationFilter(null)}
                    />
                  </div>
                </FormControl>
              </Grid>
            )}

            {hasSelectedProps && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel>
                    <Typography variant="overline">Active Filters</Typography>
                  </FormLabel>

                  <div>
                    <PropChips
                      props={[
                        ...Array.from(selectedEventProps)
                          .filter((p) => p !== allEventPropsOption)
                          .sort(sortPropsFn),
                        ...Array.from(selectedUserProps)
                          .filter((p) => p !== allUserPropsOption)
                          .sort(sortPropsFn),
                        ...Array.from(selectedGroupProps)
                          .filter((p) => p !== allGroupPropsOption)
                          .sort(sortPropsFn),
                      ]}
                      onDelete={handleOnDelete}
                      showNoneMessage={false}
                    />
                  </div>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </nav>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <ColumnLabelWithTooltip
                    label="Destination"
                    tooltip="Active destination configured in Freshpaint"
                  />
                </TableCell>
                <TableCell>
                  <ColumnLabelWithTooltip
                    label="Event Properties"
                    tooltip="Track event properties in the destination's HIPAA allowlist"
                  />
                </TableCell>
                <TableCell>
                  <ColumnLabelWithTooltip
                    label="User Properties"
                    tooltip="User (Identify) properties in the destination's HIPAA allowlist"
                  />
                </TableCell>
                <TableCell>
                  <ColumnLabelWithTooltip
                    label="Group Properties"
                    tooltip="Group properties in the destination's HIPAA allowlist"
                  />
                </TableCell>
                <TableCell>{/* Misconfiguration Warning */}</TableCell>
                <TableCell>{/* Actions */}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row) => {
                return (
                  <TableRow
                    key={`phi-allow-list-table-${row.activeDestination.id}`}
                  >
                    <TableCell>
                      <Link to={row.destinationMetadata.configPageUrl()}>
                        <Typography variant="body1">
                          <DestinationLogo
                            destinationName={row.destinationMetadata.key}
                          />
                          {row.activeDestination.name}
                        </Typography>
                      </Link>
                    </TableCell>
                    <TableCell style={{ maxWidth: "15em" }}>
                      <PropChips props={Array.from(row.eventProps)} />
                    </TableCell>
                    <TableCell style={{ maxWidth: "15em" }}>
                      <PropChips props={Array.from(row.userProps)} />
                    </TableCell>
                    <TableCell style={{ maxWidth: "15em" }}>
                      <PropChips props={Array.from(row.groupProps)} />
                    </TableCell>
                    <TableCell>
                      {row.validity.validity !== DestinationValidity.valid && (
                        <Risk
                          description={
                            row.validity.warningMessage ||
                            "This destination is misconfigured."
                          }
                          riskLevel={
                            row.validity.validity ===
                            DestinationValidity.partiallyInvalid
                              ? "medium"
                              : "high"
                          }
                          riskDisplayNameOverride={configInvalidText(
                            row.validity.validity
                          )}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Link to={row.destinationMetadata.configPageUrl()}>
                        <Button
                          fullWidth
                          color="primary"
                          size="small"
                          variant="outlined"
                        >
                          Manage
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default PHIAllowListTab;
